import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Menu,
  CalendarDays,
  Palette,
  Bell,
  BellOff,
  LogOut,
  Sparkles,
  ListChecks,
  BookOpen,
  Sticker,
  Mail,
  Settings,
  MessageSquareHeart,
  ShieldCheck,
  FileText,
  Crown,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { requestNotificationPermission, notifySupported } from "@/lib/notifications";
import { toast } from "sonner";

export default function MenuSheet() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    notifySupported() ? Notification.permission : "unsupported"
  );
  const location = useLocation();

  const enableNotifs = async () => {
    const res = await requestNotificationPermission();
    setNotifPerm(res);
    if (res === "granted") toast.success("Notifications enabled");
    else if (res === "denied") toast.error("Notifications blocked in browser settings");
    else if (res === "unsupported") toast.error("Notifications not supported on this device");
  };

  const navItems = [
    { to: "/", label: "Planner", icon: CalendarDays },
    { to: "/checklist", label: "Daily checklist", icon: ListChecks },
    { to: "/log", label: "Personal log", icon: BookOpen },
    { to: "/letters", label: "Letters to future self", icon: Mail },
    { to: "/vision", label: "Vision board", icon: Sticker },
    { to: "/sunday-reset", label: "Sunday reset", icon: Sparkles },
    { to: "/theme", label: "Theme", icon: Palette },
    { to: "/upgrade", label: "Lumivera Premium", icon: Crown },
    { to: "/review", label: "Leave a review", icon: MessageSquareHeart },
    { to: "/privacy", label: "Privacy policy", icon: ShieldCheck },
    { to: "/terms", label: "Terms of service", icon: FileText },
    ...(user?.role === "admin"
      ? [
          { to: "/admin/stats", label: "Admin · User stats", icon: Users },
          { to: "/admin/reviews", label: "Admin · Reviews", icon: ShieldCheck },
        ]
      : []),
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-10 rounded-xl"
          data-testid="menu-button"
          aria-label="Open menu"
        >
          <Menu strokeWidth={1.5} className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-72 p-0 flex flex-col h-dvh max-h-dvh overflow-hidden"
        data-testid="menu-sheet"
      >
        <SheetHeader className="p-6 pb-4 text-left shrink-0">
          <SheetTitle className="font-display text-2xl flex items-center gap-2">
            <CalendarDays strokeWidth={1.5} className="size-6 text-primary" />
            Lumivera
          </SheetTitle>
          <SheetDescription className="text-xs">
            Signed in as <span className="font-semibold text-foreground">{user?.name}</span>
          </SheetDescription>
        </SheetHeader>
        <Separator className="shrink-0" />
        {/* Scrollable region — combines nav + footer so the user can always
            reach Sign out even when iOS Safari's URL bar overlays the bottom.
            On desktop (sm+) we restore the original zero extra bottom padding. */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col pb-[calc(env(safe-area-inset-bottom)+24px)] sm:pb-0"
        >
          <nav className="p-4 space-y-1">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={`menu-link-${it.label.toLowerCase()}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-secondary"
                }`}
              >
                <Icon strokeWidth={1.5} className="size-4" />
                {it.label}
                {active && (
                  <Sparkles strokeWidth={1.5} className="size-3.5 ml-auto opacity-70" />
                )}
              </Link>
            );
          })}
        </nav>
        <Separator />
        <div className="p-4 space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={enableNotifs}
            className="w-full justify-start rounded-xl"
            data-testid="menu-enable-notifications"
          >
            {notifPerm === "granted" ? (
              <>
                <Bell strokeWidth={1.5} className="size-4 mr-2" />
                Notifications on
              </>
            ) : (
              <>
                <BellOff strokeWidth={1.5} className="size-4 mr-2" />
                Enable notifications
              </>
            )}
          </Button>
          <Button
            variant="outline"
            asChild
            size="sm"
            className="w-full justify-start rounded-xl"
            data-testid="menu-link-settings"
          >
            <Link to="/settings" onClick={() => setOpen(false)}>
              <Settings strokeWidth={1.5} className="size-4 mr-2" />
              Settings
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await logout();
              setOpen(false);
            }}
            className="w-full justify-start rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
            data-testid="menu-logout"
          >
            <LogOut strokeWidth={1.5} className="size-4 mr-2" />
            Sign out
          </Button>
        </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
